import React, { memo } from 'react';
export const modalsContent: Record<string, any> = {
  bitter: {
    title: 'The Warm Bitter Ritual',
    sub: '30 seconds every morning. Doubles bile flow within 48 hours.',
    steps: ['Heat 8 oz of water to warm (not boiling) — about 100°F.', 'Add ½ tsp of organic Dandelion Root powder OR 5 drops of Gentian tincture.', 'Optionally add a squeeze of lemon and a pinch of mineral salt.', 'Drink 15 minutes before your first meal of the day.', 'Do NOT add sweetener. The bitter taste is the activating signal to your liver.']
  },
  lymph: {
    title: 'The 3-Point Lymph Press',
    sub: '2 minutes. Manual override to clear cellular waste instantly.',
    steps: ['NECK: Apply gentle pressure with flat fingers just below your ear. Pulse 10x downward toward your collarbone.', 'ARMPIT: Reach across your chest. Press into the armpit gently and pump 10x toward your chest wall.', 'GROIN: Seated. Apply flat palm pressure at the hip crease. Pump 10x downward toward your inner thigh.', 'Repeat on both sides. Breathe deeply during each movement.', 'Do this 2x daily: morning and after sitting for 3+ hours.']
  },
  brush: {
    title: 'Pro Dry Brushing Protocol',
    sub: 'Activates the skin — your largest emunctory. Use before showering.',
    steps: ['Use a natural bristle brush with a long handle.', 'Start at your feet. Use long, firm strokes toward your heart.', 'Brush legs → thighs → stomach (circular, clockwise) → chest.', 'Switch to arms: start at hands, stroke toward your shoulders.', 'Shower immediately after to wash away released toxins.', 'Do Days 6-7 (Phase 3) plus ongoing 3x per week for maintenance.']
  },
  mag: {
    title: 'Magnesium-Potassium Night Drink',
    sub: 'Gently flushes the colon. The foundation of Phase 1.',
    steps: ['Mix 300mg Magnesium Glycinate (powder preferred) in 10 oz warm water.', 'Add ¼ tsp Cream of Tartar (natural potassium source).', 'Add a squeeze of lemon and tiny pinch of sea salt.', 'Drink 30-60 minutes before bed on Days 1-3.', "Expect a gentle bowel movement in the morning — that's the goal.", 'Reduce to 200mg Mag from Day 4 onward for maintenance.']
  },
  timer: {
    title: 'Ritual Timer',
    sub: 'Use this to time your morning rituals',
    steps: [],
    isTimer: true
  },
  plan30: {
    title: '30-Day Action Plan',
    sub: 'Your week-by-week roadmap after the 7-Day Reset',
    steps: ["WEEK 1: The 7-Day Reset (you're doing this now). Open emunctories. Open the gates.", 'WEEK 2: Maintenance Mode. Continue bitter morning ritual. 3-Point Press daily. No seed oils.', 'WEEK 3: Deepening. Add weekly castor oil liver pack (45 min Saturday). Increase sulfur foods.', 'WEEK 4: Sealing. Full dry brushing 3x/week. Monthly Magnesium flush. Your new baseline.', 'ONGOING: Never skip the morning bitter. Your liver now runs 2x more efficiently. Protect it.']
  },
  recipe1: {
    title: 'Liver Greens Bowl',
    sub: 'Lunch · 15 min prep · High sulfur + fiber · Anti-bloat, bile-stimulating',
    steps: ['BASE: 2 cups arugula + 1 cup shredded radicchio (bitter greens activate bile)', 'PROTEIN: 4 oz grilled chicken or 2 soft-boiled eggs', 'SULFUR: ½ cup steamed broccoli + ¼ cup raw red onion', 'FAT: 1 tbsp extra virgin olive oil + ½ avocado', 'DRESSING: Lemon juice + 1 tsp apple cider vinegar + pinch of sea salt', 'Eat slowly. Chew 20x per bite. Let bile do its job.']
  },
  recipe2: {
    title: 'Bitter Ginger Tonic',
    sub: 'Morning · 5 min · Liver wake-up call · Anti-inflammatory',
    steps: ['Boil 8 oz of water. Let cool to warm (not hot).', 'Grate 1-inch fresh ginger root into a mug', 'Add 5 drops of Gentian tincture OR ½ tsp Dandelion root powder', 'Squeeze ½ a lemon. Add 1 pinch cayenne (optional).', 'Do NOT sweeten. The bitterness is the medicine.', 'Drink 15 min before breakfast. Repeat before lunch for double effect.']
  },
  recipe3: {
    title: 'Sulfur Detox Plate',
    sub: 'Dinner · 20 min · Supports Liver Phase II Detox · Gut health',
    steps: ['PROTEIN: 5 oz wild salmon OR 1 cup lentils (sulfur amino acids)', 'SULFUR VEG: Roasted broccoli + cauliflower + garlic (high in glutathione precursors)', 'ROOT: ½ cup steamed beets (supports Phase II liver pathway)', 'HERB: Fresh cilantro and parsley (heavy metal binders)', 'DRESS: Drizzle with olive oil + lemon + turmeric paste', 'Eat dinner before 7 PM. Fasting window begins at 7 PM on Days 4-7.']
  }
};
export const chaptersContent: Record<string, any> = {
  welcome: {
    title: 'Welcome: The Day the Mirror Lied No More',
    content: <div className="prose text-text-dark text-base leading-relaxed pb-24" data-id="element-389">
        <p className="mb-4" data-id="element-390">
          Remember that morning? You woke up, walked to the bathroom, and didn't
          recognize the person staring back. Your eyes were puffy, your jawline
          seemed to have vanished into your neck, and no matter how much water
          you drank the day before, you felt "full." Not full of food — full of{' '}
          <em data-id="element-391">stagnation</em>.
        </p>
        <p className="mb-4" data-id="element-392">
          You probably blamed yourself. You thought about the glass of wine you
          had two nights ago, or the fact that you skipped your workout. You
          told yourself you just needed more discipline. More cardio. More
          restriction.
        </p>
        <div className="bg-gradient-to-br from-[#F0F7F1] to-[#E4F0E6] border-l-4 border-sage-dark rounded-r-xl p-4 my-5 italic text-sage-dark font-semibold" data-id="element-393">
          "Your body doesn't need more input. It needs better output."
        </div>
        <p className="mb-4" data-id="element-394">
          For years, you've been told the answer is "more." More exercise, more
          lemon water, more expensive green juices. But the truth is, your body
          doesn't need more input. It needs better <strong data-id="element-395">output</strong>.
        </p>
        <p className="mb-4" data-id="element-396">
          I spent a decade watching people struggle with "detoxes" that left
          them with migraines, skin breakouts, and zero energy. Why? Because
          they were trying to flush a house with clogged pipes. In this program,
          we aren't going to "push" toxins. We are going to{' '}
          <strong data-id="element-397">open the gates</strong>.
        </p>
        <p className="mb-4" data-id="element-398">
          Think about a bathtub with the drain plugged and the faucet running.
          The water level keeps rising. Traditional diets tell you to turn the
          faucet down—eat less, restrict more. But the water is still stagnant.
          The real solution? Unplug the drain.
        </p>
        <p className="mb-4" data-id="element-399">
          That is exactly what we are going to do over the next 7 days. We are
          going to reactivate your emunctories — the natural exit routes for
          waste — so your body can finally breathe again.
        </p>
        <div className="bg-gradient-to-br from-[#FFF8E8] to-[#FFF0D0] rounded-xl p-4 my-5 text-center" data-id="element-400">
          <span className="text-4xl font-extrabold text-gold block" data-id="element-401">7</span>
          <div className="text-sm text-text-mid mt-1" data-id="element-402">
            Days to reactivate your body's natural drainage system
          </div>
        </div>
        <p className="mb-4" data-id="element-403">
          This isn't about starvation. It's about strategic activation. You will
          eat real food. You will nourish your cells. But more importantly, you
          will signal to your body that it is safe to let go of the waste it has
          been holding onto for protection.
        </p>
        <p className="mb-4" data-id="element-404">
          When your liver is overwhelmed, it holds onto water to dilute the
          toxins it can't process. That "weight" you feel? A significant portion
          of it isn't fat. It's protective fluid. It's cellular exhaust that
          hasn't found the exit.
        </p>
        <p className="mb-4" data-id="element-405">
          By the end of these 7 days, your jeans will fit differently, your
          brain fog will lift, and your skin will regain that glow you thought
          was gone forever. You will wake up with a flat stomach and clear eyes,
          not because you starved yourself, but because your internal plumbing
          is finally flowing the way nature intended.
        </p>
        <p className="mb-4" data-id="element-406">
          Take a deep breath. The hard part—the endless cycle of restriction and
          guilt—is over. You have the code now.
        </p>
        <p className="font-bold text-sage-dark text-lg mt-8" data-id="element-407">
          Let's get to work.
        </p>
      </div>
  },
  ch1: {
    title: 'The Myth of the "Magic Juice"',
    content: <div className="prose text-text-dark text-base leading-relaxed pb-24" data-id="element-408">
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-409">
          Why Your Previous Detoxes Failed
        </h3>
        <p className="mb-4" data-id="element-410">
          The "detox industry" has a dirty secret: most programs are designed
          for failure. They sell you a $200 box of juices and promise a new you
          in three days. And sure, you might lose 4 pounds of water weight, but
          how did you feel during those three days? Miserable. Cold. Irritable.
          And the moment you ate a real meal, the weight came rushing back.
        </p>
        <p className="mb-4" data-id="element-411">
          When you drink nothing but green juice for three days, you release
          toxins from your fat cells into your bloodstream. Your body goes into
          panic mode. But if your liver is sluggish and your lymphatic system is
          stagnant, those toxins have <strong data-id="element-412">nowhere to go</strong>.
        </p>
        <p className="mb-4" data-id="element-413">
          They recirculate. This is why you get the "detox headache," the "keto
          flu," or those painful chin breakouts. Your body is desperately trying
          to push waste out through your skin or your head because the primary
          exits—your liver and colon—are locked shut.
        </p>
        <div className="bg-gradient-to-br from-[#FFF8E8] to-[#FFF0D0] rounded-xl p-4 my-5 text-center" data-id="element-414">
          <span className="text-4xl font-extrabold text-gold block" data-id="element-415">84%</span>
          <div className="text-sm text-text-mid mt-1" data-id="element-416">
            of Americans in urban areas have "bottlenecked" drainage pathways
            due to microplastics and chronic cortisol (2025 data)
          </div>
        </div>
        <p className="mb-4" data-id="element-417">
          You aren't "fat" — you are <strong data-id="element-418">inflamed and backed up</strong>.
          Pushing a detox through a clogged system is like trying to force 10
          gallons of water through a 1-inch pipe. It creates pressure, not flow.
          It creates stress, not healing.
        </p>
        <p className="mb-4" data-id="element-419">
          Think about a traffic jam on a major highway. If there's an accident
          blocking all three lanes, sending more cars onto the highway doesn't
          help. It just makes the jam worse. Your liver is that highway. When
          it's blocked by environmental toxins, processed seed oils, and chronic
          stress, sending a massive wave of "detox juice" into the system just
          causes a massive biological pile-up.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-420">
          The Shift: Drainage Before Detox
        </h3>
        <p className="mb-4" data-id="element-421">
          To get results, we must flip the script. We focus on the{' '}
          <strong data-id="element-422">Drainage Hierarchy</strong>. Think of your body like a
          skyscraper. If the trash chute at the bottom is blocked, it doesn't
          matter how much cleaning you do on the top floors — the trash will
          pile up in the hallways.
        </p>
        <p className="mb-4" data-id="element-423">
          The cells in your brain (the penthouse) dump waste into your lymphatic
          system. Your lymphatic system dumps waste into your liver. Your liver
          dumps waste into your colon. Your colon dumps waste into the toilet.
        </p>
        <p className="mb-4" data-id="element-424">
          If you are constipated, the colon is blocked. The liver can't dump its
          waste. The lymph backs up. The brain gets foggy. You cannot fix the
          penthouse without first clearing the basement.
        </p>
        <div className="bg-gradient-to-br from-[#F0F7F1] to-[#E4F0E6] border-l-4 border-sage-dark rounded-r-xl p-4 my-5 italic text-sage-dark font-semibold" data-id="element-425">
          "Health is an evacuation process, not just an infusion process."
        </div>
        <p className="mb-4" data-id="element-426">
          This is the "Aha!" moment my clients find most powerful: the
          realization that health is an evacuation process, not just an infusion
          process. We spend so much time obsessing over what we put *into* our
          bodies—the right supplements, the perfect organic kale, the exact
          macro ratios. But we spend zero time thinking about how things get
          *out*.
        </p>
        <p className="mb-4" data-id="element-427">
          Once the gates are open, the weight starts to drop autonomously
          because the body no longer needs to hold onto "protective water" to
          dilute those trapped toxins. Your metabolism speeds up because your
          thyroid isn't fighting through a swamp of cellular debris.
        </p>
        <p className="mb-4" data-id="element-428">
          In this chapter we introduce the concept of{' '}
          <strong data-id="element-429">Emunctories</strong>: your liver, kidneys, colon, lungs, and
          skin. Before we even talk about recipes, we must ensure these exits
          are wide open. We must clear the highway before we send the cars.
        </p>
        <p className="mb-4" data-id="element-430">
          Stop blaming your willpower. Your willpower is fine. Your plumbing is
          just backed up. Let's fix the plumbing.
        </p>
      </div>
  },
  ch2: {
    title: "The Emunctories — Your Body's Waste Management",
    content: <div className="prose text-text-dark text-base leading-relaxed pb-24" data-id="element-431">
        <p className="mb-4" data-id="element-432">
          The word "emunctories" comes from the Latin word <em data-id="element-433">emungere</em>,
          which means "to blow one's nose" or "to cleanse." In biological terms,
          your emunctories are your organs of elimination. They are the doors
          through which waste leaves your house.
        </p>
        <p className="mb-4" data-id="element-434">
          You have five primary emunctories: the colon, the liver, the kidneys,
          the lungs, and the skin. In a perfect world, these five organs work in
          harmony, passing waste from one to the other like a well-oiled relay
          team. But we don't live in a perfect world. We live in a world of
          endocrine disruptors, blue light, and sitting at desks for 10 hours a
          day.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-435">
          The Liver: The Grand Filter
        </h3>
        <p className="mb-4" data-id="element-436">
          Your liver performs over <strong data-id="element-437">500 functions</strong>, but its
          primary job in 2026 is managing the chemical load of the modern world.
          It is the grand filter of your bloodstream. Every drop of blood that
          leaves your digestive tract must pass through the liver before it goes
          anywhere else.
        </p>
        <p className="mb-4" data-id="element-438">
          When the liver is overwhelmed, it stops burning fat and starts storing
          it — specifically around the midsection — to "protect" your vital
          organs from circulating waste. Fat is a safe storage facility for
          toxins. If your liver can't process a chemical, it wraps it in fat and
          shoves it in the closet (your belly or thighs) to deal with later.
        </p>
        <div className="bg-gradient-to-br from-[#FFF8E8] to-[#FFF0D0] rounded-xl p-4 my-5 text-center" data-id="element-439">
          <span className="text-4xl font-extrabold text-gold block" data-id="element-440">500+</span>
          <div className="text-sm text-text-mid mt-1" data-id="element-441">
            Functions your liver performs every single day
          </div>
        </div>
        <p className="mb-4" data-id="element-442">
          To fix this, we don't need a harsh detox. We need{' '}
          <strong data-id="element-443">Bitter Activation</strong>. For thousands of years, humans ate
          wild, bitter plants before meals. This bitterness was a signal to the
          liver to wake up and produce bile. Today, our diets are entirely sweet
          and salty. We have bred the bitterness out of our food, and as a
          result, our livers are asleep at the wheel.
        </p>
        <p className="mb-4" data-id="element-444">
          By introducing specific bitter compounds 15 minutes before meals, we
          stimulate bile flow. Bile is the "soap" of the body; it carries toxins
          out of the liver and into the gut for removal. Without bile flow,
          there is no detox. Toxins just sit in the liver, causing inflammation
          and fatigue.
        </p>
        <div className="bg-gradient-to-br from-[#F0F7F1] to-[#E4F0E6] border-l-4 border-sage-dark rounded-r-xl p-4 my-5 italic text-sage-dark font-semibold" data-id="element-445">
          "Bile is the soap of the body. Without it, nothing gets clean."
        </div>
        <p className="mb-4" data-id="element-446">
          You will learn the <strong data-id="element-447">Warm Bitter Ritual</strong> that takes 30
          seconds but doubles your liver's efficiency within 48 hours. It is the
          single most important habit you will build in this program.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-448">
          The Lymph: Your Body's Sewage System
        </h3>
        <p className="mb-4" data-id="element-449">
          If the liver is the filter, the lymphatic system is the sewage pipes.
          You have three times more lymph fluid in your body than blood. It
          bathes every cell, delivering nutrients and carrying away cellular
          exhaust.
        </p>
        <p className="mb-4" data-id="element-450">
          But there is a massive design flaw: unlike your cardiovascular system,
          which has a heart to pump blood, your lymphatic system{' '}
          <strong data-id="element-451">does not have a pump</strong>. It relies entirely on{' '}
          <strong data-id="element-452">movement and pressure</strong> (specifically muscle
          contraction and deep diaphragmatic breathing) to move fluid upward
          against gravity.
        </p>
        <p className="mb-4" data-id="element-453">
          If you sit at a desk for 6 hours a day, your lymph becomes like a
          stagnant pond — thick, murky, and full of cellular debris. This is the
          root of "puffy face," leg heaviness, and that stiff feeling when you
          wake up in the morning.
        </p>
        <p className="mb-4" data-id="element-454">
          We will implement the <strong data-id="element-455">3-Point Press</strong> technique.
          Throughout the day, we will manually activate the main lymphatic
          junctions (neck, armpits, groin) to keep the "fluid electricity"
          moving. By gently pressing and pumping these specific nodes, we create
          a vacuum effect that pulls stagnant fluid out of your tissues and back
          into circulation where the liver can filter it.
        </p>
        <p className="mb-4" data-id="element-456">
          This isn't a workout; it's a <em data-id="element-457">2-minute manual override</em> to
          clear the waste. When you combine liver bitters with lymph movement,
          the transformation is visible in your face almost instantly. You will
          literally watch your cheekbones reappear.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-458">
          The Colon, Kidneys, and Skin
        </h3>
        <p className="mb-4" data-id="element-459">
          The colon is the final exit door. If you are not having 1-2 complete
          bowel movements a day, you are reabsorbing toxins. We will use
          specific magnesium protocols to ensure this door is wide open.
        </p>
        <p className="mb-4" data-id="element-460">
          The kidneys filter water-soluble waste. We support them simply by
          drinking enough clean, mineralized water. Plain water isn't enough;
          without minerals, water flushes right through you without entering the
          cells.
        </p>
        <p className="mb-4" data-id="element-461">
          Finally, the skin. It is your largest organ and your backup detox
          pathway. When the liver and colon are overwhelmed, the body pushes
          waste out through the skin (hello, acne and eczema). We will use dry
          brushing to keep the skin's pores open and stimulate the superficial
          lymph vessels just beneath the surface.
        </p>
        <p className="mb-4" data-id="element-462">
          When all five emunctories are open and communicating, your body
          becomes a self-cleaning oven. You don't have to force it. You just
          have to get out of its way.
        </p>
      </div>
  },
  ch3: {
    title: 'The 7-Day Reset — Step by Step',
    content: <div className="prose text-text-dark text-base leading-relaxed pb-24" data-id="element-463">
        <p className="mb-4" data-id="element-464">
          This is the roadmap. No guesswork, no starving, no complicated math.
          Just a systematic, biological unblocking of your body's pathways. We
          do this in three distinct phases. You cannot skip ahead. The order is
          everything.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-465">
          Phase 1: Preparation & Opening (Days 1–2)
        </h3>
        <p className="mb-4" data-id="element-466">
          The biggest mistake people make is jumping straight into a heavy detox
          without opening the exit doors first. If you start killing off bad
          bacteria or releasing stored toxins while your colon is sluggish, you
          will feel terrible.
        </p>
        <p className="mb-4" data-id="element-467">
          Focus on hydration with minerals and opening the colon.{' '}
          <strong data-id="element-468">You cannot detox if you are constipated.</strong> We use a
          specific Magnesium-Potassium ratio to "flush the pipes" gently before
          moving to deeper layers. Magnesium draws water into the bowel, while
          potassium ensures cellular hydration.
        </p>
        <div className="bg-gradient-to-br from-[#F0F7F1] to-[#E4F0E6] border-l-4 border-sage-dark rounded-r-xl p-4 my-5 italic text-sage-dark font-semibold" data-id="element-469">
          "The colon must move before anything else works. This is
          non-negotiable."
        </div>
        <p className="mb-4" data-id="element-470">
          Days 1-2 feel gentle and easy. You're not restricting food. You're
          adding: the Warm Bitter Ritual in the morning, mineral water
          throughout the day, and the magnesium drink at night. Simple.
          Powerful. You might feel a slight headache on Day 2 as your body
          realizes the gates are opening and begins to mobilize surface-level
          waste. Drink more mineral water.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-471">
          Phase 2: Activation & Drainage (Days 3–5)
        </h3>
        <p className="mb-4" data-id="element-472">
          Now that the bottom floor is clear, we can start sweeping the upper
          levels. Your liver is ready to work, and your lymph is primed.
        </p>
        <p className="mb-4" data-id="element-473">
          Here we introduce the <strong data-id="element-474">Drainage Meals</strong>. These aren't
          tiny salads that leave you starving. They are robust meals high in
          specific fibers that bind to bile, high in sulfur (to support the
          liver's Phase II detoxification pathway), and low in inflammatory fats
          (no seed oils). This is where the "Bloat-Free" feeling becomes
          permanent.
        </p>
        <p className="mb-4" data-id="element-475">
          Sulfur is the key here. Foods like broccoli, onions, garlic, and eggs
          provide the raw materials your liver needs to create glutathione, the
          master antioxidant. Glutathione acts like a biological magnet,
          grabbing onto heavy metals and plastics and dragging them out of your
          system.
        </p>
        <div className="bg-gradient-to-br from-[#FFF8E8] to-[#FFF0D0] rounded-xl p-4 my-5 text-center" data-id="element-476">
          <span className="text-4xl font-extrabold text-gold block" data-id="element-477">Day 4</span>
          <div className="text-sm text-text-mid mt-1" data-id="element-478">
            Most clients report visible facial de-puffing and first signs of
            flat stomach by Day 4
          </div>
        </div>
        <p className="mb-4" data-id="element-479">
          The 3-Point Lymph Press is now twice daily. You'll start noticing your
          rings feel looser in the morning, and your jawline will begin to
          re-emerge. This is lymphatic drainage working in real time. You are
          literally draining the swamp.
        </p>
        <p className="mb-4" data-id="element-480">
          During this phase, we also implement a gentle 12-hour overnight fast.
          If you finish dinner at 7 PM, you don't eat again until 7 AM. This
          gives your migrating motor complex (the sweeping mechanism of your
          gut) time to clean house while you sleep.
        </p>
        <h3 className="text-lg font-bold mt-6 mb-3" data-id="element-481">
          Phase 3: Deep Refresh & Sealing (Days 6–7)
        </h3>
        <p className="mb-4" data-id="element-482">
          The deep work is happening. Now we accelerate the removal of the waste
          that has been mobilized. We turn to the skin.
        </p>
        <p className="mb-4" data-id="element-483">
          We seal the progress. We introduce <strong data-id="element-484">dry brushing</strong> and{' '}
          <strong data-id="element-485">infrared heat</strong> (or hot Epsom salt baths) to use the
          skin — the largest emunctory — to release the final heavy load. Dry
          brushing sweeps the superficial lymph vessels just under the skin,
          pushing fluid toward the heart for processing.
        </p>
        <p className="mb-4" data-id="element-486">
          You will notice your energy levels soaring. The brain fog that used to
          hit at 2 PM is completely gone. Your sleep will be deeper than it has
          been in years, because your liver isn't waking you up at 3 AM
          struggling to process a backlog of toxins.
        </p>
        <p className="mb-4" data-id="element-487">
          You will wake up on Day 7 feeling like a{' '}
          <em data-id="element-488">different human being</em>. Not because of a magic powder, but
          because you systematically opened every exit route your body has. You
          respected the biological hierarchy.
        </p>
        <div className="bg-gradient-to-br from-[#F0F7F1] to-[#E4F0E6] border-l-4 border-sage-dark rounded-r-xl p-4 my-5 italic text-sage-dark font-semibold" data-id="element-489">
          "Day 7 is not the end. It's your new starting point."
        </div>
        <p className="mb-4" data-id="element-490">
          This protocol isn't something you do once a year to punish yourself
          for a vacation. It is a toolkit. You now know exactly how to open your
          pathways. You have the keys to the gates.
        </p>
      </div>
  },
  conclusion: {
    title: 'You Are Now in Flow',
    content: <div className="prose text-text-dark text-base leading-relaxed pb-24" data-id="element-491">
        <p className="mb-4" data-id="element-492">
          You've done it. You've stopped fighting your body and started working
          with its brilliant, biological design. You didn't starve yourself. You
          didn't punish yourself on a treadmill for hours. You simply removed
          the roadblocks.
        </p>
        <p className="mb-4" data-id="element-493">
          The puffiness in your eyes? Gone. The mid-day slump? Replaced by a
          steady, clean energy that lasts from morning until night. The bloating
          that used to dictate what you wore? A distant memory.
        </p>
        <p className="mb-4" data-id="element-494">
          Look in the mirror today. Really look. That clarity in your skin and
          the sharpness in your jawline? That is what your body looks like when
          it isn't drowning in its own cellular exhaust. That is your baseline.
        </p>
        <div className="bg-gradient-to-br from-[#FFF8E8] to-[#FFF0D0] rounded-xl p-4 my-5 text-center" data-id="element-495">
          <span className="text-4xl font-extrabold text-gold block" data-id="element-496">7</span>
          <div className="text-sm text-text-mid mt-1" data-id="element-497">
            Days that changed everything. Now it's your new normal.
          </div>
        </div>
        <p className="mb-4" data-id="element-498">
          You aren't just "cleaner" — you are more in tune with the signals your
          body is sending. When you feel a headache coming on, you don't
          immediately reach for a pill; you ask yourself, "Are my pathways open?
          Have I moved my lymph today?"
        </p>
        <p className="mb-4" data-id="element-499">
          You've learned that{' '}
          <strong data-id="element-500">health isn't a destination; it's a state of flow</strong>.
          Life will happen. You will eat cake. You will drink wine. You will
          have stressful weeks. But now, you know exactly how to hit the reset
          button. You know how to open the gates.
        </p>
        <div className="bg-gradient-to-br from-[#F0F7F1] to-[#E4F0E6] border-l-4 border-sage-dark rounded-r-xl p-4 my-5 italic text-sage-dark font-semibold" data-id="element-501">
          "As long as you keep the gates open, your body will do exactly what it
          was designed to do: thrive."
        </div>
        <p className="mb-4" data-id="element-502">
          Keep your morning bitter ritual. Keep your 3-point press. Protect your
          peace, and protect your liver. Welcome to the flow.
        </p>
      </div>
  }
};